export function formatFileSize(
  bytes: number | undefined | null,
  decimals = 2,
): string {
  if (bytes === undefined || bytes === null) return 'Unknown size';
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

export const formatBytes = formatFileSize;

interface FormatDateConfig {
  fallback?: string;
  formatter?: 'date' | 'datetime';
  includeDefaultDateOptions?: boolean;
  locale?: string | string[];
}

export function formatDate(
  dateString: string | number | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  config?: FormatDateConfig,
): string {
  const fallback = config?.fallback ?? '-';
  if (dateString === null || dateString === undefined) {
    return fallback;
  }

  const date = dateString instanceof Date ? dateString : new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  const locale = config && 'locale' in config ? config.locale : 'en-US';
  const formatter =
    config?.formatter ??
    (options &&
    (options.hour !== undefined ||
      options.minute !== undefined ||
      options.second !== undefined ||
      options.timeStyle !== undefined)
      ? 'datetime'
      : 'date');
  const includeDefaultDateOptions =
    config?.includeDefaultDateOptions ?? formatter === 'date';
  const defaultDateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const resolvedOptions = includeDefaultDateOptions
    ? {
        ...defaultDateOptions,
        ...options,
      }
    : options;

  if (!resolvedOptions || Object.keys(resolvedOptions).length === 0) {
    return formatter === 'datetime'
      ? date.toLocaleString(locale)
      : date.toLocaleDateString(locale);
  }

  return formatter === 'datetime'
    ? date.toLocaleString(locale, resolvedOptions)
    : date.toLocaleDateString(locale, resolvedOptions);
}

/** `8 Sep 2026` (en-US month names: en-GB renders September as "Sept"); '' when missing/invalid. */
export function formatShortDate(
  value: string | number | Date | null | undefined,
): string {
  if (value === null || value === undefined) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;
}

export function formatDateTimeWithLocalZone(
  dateString: string | number | Date | null | undefined,
): string {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

  return formatDate(
    dateString,
    {
      timeZone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    },
    {
      fallback: '-',
      formatter: 'datetime',
      locale: undefined,
    },
  );
}
